import React, { useEffect, useState } from 'react';
import { Modal } from 'src/components/Modal/Modal';
import { ModalHeader } from 'src/components/Modal/ModalHeader';
import { ModalBody } from 'src/components/Modal/ModalBody';
import { ProductLocations } from 'src/components/ProductDetail/ProductLocations';
import { Button } from 'src/components/Button/Button';
import { ApplicationMethod, ProductLocation } from 'src/components/CustomisationPage/feature/customisation.types';
import { ProductPrice } from 'src/types/global-types';
import { TierPrice } from 'src/components/ProductDetail/TieredPricing';
import { Svg } from 'src/components/Svg/Svg';
import { getPriceEstimates } from 'src/components/ProductDetail/utils/priceEstimates';
import { PriceEstimate } from 'src/components/ProductDetail/utils/product-detail.types';

import classes from './CustomisationSidebar.scss';

export type CustomisationSidebarProps = {
    locations: ProductLocation[];
    price: ProductPrice;
    tieredPricing?: TierPrice[];
    quantity?: number;
    price_estimates: PriceEstimate[];
    onlyEmbroideryLocation?: boolean;
    onlyPrintLocation?: boolean;
};

const defaultEstimatePrices = {
    embroideryItemPrice: 0,
    embroideryTotalPrice: 0,
    printItemPrice: 0,
    printTotalPrice: 0,
};

export const CustomisationSidebar: React.FC<CustomisationSidebarProps> = ({
    locations,
    price,
    quantity,
    tieredPricing,
    price_estimates,
    onlyEmbroideryLocation = false,
    onlyPrintLocation = false,
}) => {
    const defaultApplicationMethod = onlyEmbroideryLocation ? ApplicationMethod.Embroidery : ApplicationMethod.Print;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedQuantity, setSelectedQuantity] = useState(1);
    const [singlePrice, setSinglePrice] = useState(0);
    const [applicationMethod, setApplicationMethod] = useState<ApplicationMethod>(defaultApplicationMethod);
    const [estimatePrices, setEstimatePrices] = useState(defaultEstimatePrices);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const selectedQuantityChangeHandler = (event) => {
        const value = event.target?.value;
        setSelectedQuantity(value);
        if (value < 0) {
            setSelectedQuantity(0);
        }
        if (value > 9999) {
            event.target.value = 9999;
            setSelectedQuantity(9999);
        }
    };

    useEffect(() => {
        if (tieredPricing) {
            setSinglePrice(tieredPricing[0].price);
        } else if (price?.price) {
            setSinglePrice(price.price);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const priceForQuantity: TierPrice | ProductPrice = tieredPricing
            ? tieredPricing.filter((price, index) => {
                  const next = tieredPricing[index + 1];
                  const nextQty = (next && next.qty) ?? Infinity;
                  return selectedQuantity >= price.qty && selectedQuantity < nextQty;
              })[0] || price
            : price;
        setSinglePrice(priceForQuantity.price);
    }, [applicationMethod, price, selectedQuantity, tieredPricing]);

    useEffect(() => {
        quantity && setSelectedQuantity(quantity);
    }, [quantity]);

    useEffect(() => {
        setEstimatePrices(getPriceEstimates(price_estimates, selectedQuantity, singlePrice, false));
    }, [price_estimates, selectedQuantity, price, singlePrice]);

    const { embroideryItemPrice, printItemPrice, embroideryTotalPrice, printTotalPrice } = estimatePrices;

    return (
        <>
            <Button onClick={openModal} variant="reset" className={classes.button}>
                <Svg name="customise" />
                Logo Pricing
            </Button>
            <Modal type="sidebar" className={classes.modalContainer} isOpen={isModalOpen} onDismiss={closeModal}>
                <ModalHeader
                    title="Logo Pricing"
                    className={classes.modalHeader}
                    titleClassName={classes.modalHeaderTitle}
                    icon="customisation"
                    iconOnRight={true}
                    onDismiss={closeModal}
                />
                <ModalBody>
                    <div>
                        <p className={classes.paragraph}>
                            We offer embroidered AND/OR printed logos.
                            <br />
                            <br />
                            You can add customisations after adding your products to the basket.
                        </p>
                        <span className={classes.title}>Price with 1 x logo would be</span>
                        <div className={classes.select}>
                            {!onlyEmbroideryLocation && (
                                <div
                                    className={classes.option}
                                    onClick={() => setApplicationMethod(ApplicationMethod.Print)}
                                >
                                    <Svg
                                        name={
                                            applicationMethod === ApplicationMethod.Print
                                                ? 'checkbox-active'
                                                : 'checkbox-empty'
                                        }
                                    />
                                    <span>Print</span>
                                </div>
                            )}
                            {!onlyPrintLocation && (
                                <div
                                    className={classes.option}
                                    onClick={() => setApplicationMethod(ApplicationMethod.Embroidery)}
                                >
                                    <Svg
                                        name={
                                            applicationMethod === ApplicationMethod.Embroidery
                                                ? 'checkbox-active'
                                                : 'checkbox-empty'
                                        }
                                    />
                                    <span>Embroidery</span>
                                </div>
                            )}
                        </div>
                        <table className={classes.table}>
                            <tbody>
                                <tr>
                                    <td className={classes.cellQuantity} rowSpan={2}>
                                        <input
                                            className={classes.quantityInput}
                                            type="number"
                                            value={selectedQuantity}
                                            min="1"
                                            max="9999"
                                            maxLength={4}
                                            onChange={selectedQuantityChangeHandler}
                                        />
                                        <span className={classes.regularFont}>&nbsp;x</span>
                                    </td>
                                    <td className={classes.cellPriceEach}>
                                        <div>
                                            <div>
                                                <span className={classes.name}>Garment(s)</span>
                                            </div>
                                            <div>
                                                <span className={classes.price}>
                                                    {singlePrice ? (
                                                        <>£{singlePrice.toFixed(2)}</>
                                                    ) : (
                                                        <>£{price.price.toFixed(2)}</>
                                                    )}
                                                </span>
                                                <span className={classes.unit}> each</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className={classes.cellPriceTotal} rowSpan={2}>
                                        {applicationMethod === ApplicationMethod.Embroidery
                                            ? `£${embroideryTotalPrice.toFixed(2)}`
                                            : `£${printTotalPrice.toFixed(2)}`}
                                        <span className={classes.regularFont}>*</span>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={classes.cellPriceEach}>
                                        <div>
                                            <div>
                                                <span className={classes.name}>Logo(s)</span>
                                            </div>
                                            <div>
                                                <span className={classes.price}>
                                                    {applicationMethod === ApplicationMethod.Embroidery
                                                        ? `£${embroideryItemPrice.toFixed(2)}`
                                                        : `£${printItemPrice.toFixed(2)}`}
                                                </span>
                                                <span className={classes.unit}> each</span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <span className={classes.annotation}>
                            *Does not include setup fee or delivery. Estimates based on 1 x logo per garment.
                            <span className={classes.annotationExtra}>
                                A small extra charge is applied for{' '}
                                <a href="/customisation-prices">Big Back Embroidery and Big Back Print</a>.
                            </span>
                        </span>
                        <span className={classes.title}>Examples</span>
                        <ProductLocations newGrid={true} locations={locations} borderless={true} />
                    </div>
                </ModalBody>
            </Modal>
        </>
    );
};

export default CustomisationSidebar;
